import React, { useState, ChangeEvent } from 'react';
import styled from '@emotion/styled';
import SectionMessage from '@atlaskit/section-message';
import Button, { LoadingButton } from '@atlaskit/button';
import { Label, Field, ErrorMessage } from '@atlaskit/form';
import { RadioGroup } from '@atlaskit/radio';
import { OptionsPropType } from '@atlaskit/radio/types';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import Textfield from '@atlaskit/textfield';
import Spinner from '@atlaskit/spinner';
import { HelpContent } from '@css-xp/ppc-common';
import { CloudSiteRegExp } from '@css-xp/ppc-constants';

enum CloudSiteType {
  existing = 'existing',
  new = 'new',
}

const cloudSiteTypeOptions: OptionsPropType = [
  {
    name: 'cloudSiteType',
    value: CloudSiteType.new,
    label: 'New site',
  },
  {
    name: 'cloudSiteType',
    value: CloudSiteType.existing,
    label: 'Existing site',
  },
];

type CloudSiteSelectorState = {
  cloudSiteType?: CloudSiteType;
  selectedCloudSite: string;
  errorMessage?: string;
  isMalformedSiteUrl?: boolean;
};

type Props = {
  availableCloudsiteHostnames: string[];
  isOpen: boolean;
  closeModal: () => void;
  onChange: (cloudSiteHostname?: string) => Promise<void>;
  isLoading?: boolean;
};

const CloudsiteDropdownWrapper = styled.div({
  marginTop: '20px',
  marginBottom: '8px',
});

const initialState: CloudSiteSelectorState = {
  selectedCloudSite: '',
};

export const CloudSiteSelector = ({ isOpen, closeModal, onChange, isLoading }: Props) => {
  const [state, setState] = useState<CloudSiteSelectorState>(initialState);

  const content = (() => {
    return (
      <div className="cloudsite-modal-body-content">
        <Label htmlFor="">This new subscription is for a...</Label>
        <RadioGroup
          onChange={(data) => {
            const userSelection = data.target.value;
            const updatedCloudSiteType =
              userSelection === CloudSiteType.existing ? CloudSiteType.existing : CloudSiteType.new;

            setState((prev) => {
              return {
                ...prev,
                cloudSiteType: updatedCloudSiteType,
              };
            });
          }}
          options={cloudSiteTypeOptions}
        />

        {state.cloudSiteType === CloudSiteType.existing && (
          <CloudsiteDropdownWrapper>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
            >
              <Field name="site-url">
                {({ fieldProps }) => (
                  <>
                    <Textfield
                      placeholder="Enter URL"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const { value } = e.target;
                        fieldProps.onChange(e);
                        const patternOutput = CloudSiteRegExp.test(value);
                        setState((prev) => ({
                          ...prev,
                          selectedCloudSite: value,
                          isMalformedSiteUrl: value !== '' && !patternOutput,
                        }));
                      }}
                      isInvalid={state.isMalformedSiteUrl}
                      value={state.selectedCloudSite}
                    />
                    {state.isMalformedSiteUrl && <ErrorMessage>Enter a valid URL</ErrorMessage>}
                  </>
                )}
              </Field>
            </form>
          </CloudsiteDropdownWrapper>
        )}

        {state.errorMessage && (
          <SectionMessage title="Not Allowed" appearance="error">
            <p className="same-products-found-error-message">
              {state.errorMessage}
              <HelpContent />
            </p>
          </SectionMessage>
        )}
      </div>
    );
  })();

  const resetState = () => setState(initialState);

  const isValidExistingSite =
    state.cloudSiteType === CloudSiteType.existing &&
    (state.isMalformedSiteUrl || state.selectedCloudSite.trim() === '');
  const handleConfirm = async () => {
    if (state.cloudSiteType === CloudSiteType.new) {
      onChange();
      closeModal();
      resetState();

      return;
    }

    const userSelectedCloudsite = state.selectedCloudSite;

    // Let's keep it for the time being, in case we need this back.

    // const isValidSelection =
    //     availableCloudsiteHostnames.find((cloudsiteHostname) => cloudsiteHostname === userSelectedCloudsite) !==
    //     undefined;
    //
    // if (!isValidSelection) {
    //   setState((prev) => ({
    //     ...prev,
    //     errorMessage: 'Select a site with existing subscriptions under your billing account.',
    //   }));
    //
    //   return;
    // }

    try {
      await onChange(userSelectedCloudsite);
    } catch (err) {
      setState((prev) => ({
        ...prev,
        errorMessage: (err as any).message,
      }));
      return;
    }
    closeModal();
    resetState();
  };

  return (
    <>
      <ModalTransition>
        {isOpen && (
          <Modal onClose={closeModal}>
            <ModalHeader>
              <ModalTitle>Tell us about the site</ModalTitle>
            </ModalHeader>
            <ModalBody>{content}</ModalBody>
            <ModalFooter>
              <Button
                appearance="subtle"
                isDisabled={isLoading}
                onClick={() => {
                  closeModal();
                  resetState();
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                appearance="primary"
                type="button"
                onClick={handleConfirm}
                isLoading={isLoading}
                isDisabled={isLoading || isValidExistingSite}
              >
                Confirm
              </LoadingButton>
            </ModalFooter>
          </Modal>
        )}
      </ModalTransition>
    </>
  );
};
